import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { fetchFullCandidateReport } from "../../services/user/api";
import "../../styles/user/finalreport.css";
import logo from "../../assets/images/Logo.png";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import { useNavigate } from "react-router-dom";
import { getS3key } from "../../services/employee/api";
import PDFView from "./PDFView";

const FinalReportPage = ({ candidateId, onBack }) => {
  const [reportData, setReportData] = useState(null);
  const [currentAddressProofs, setCurrentAddressProofs] = useState([]);
  const [permanentAddressProofs, setPermanentAddressProofs] = useState([]);
  const [educationProofs, setEducationProofs] = useState({});
  const [employmentPhysicalProofs, setEmploymentPhysicalProofs] = useState({});
  const [employmentVirtualProofs, setEmploymentVirtualProofs] = useState([]);
  const [personalImage, setPersonalImage] = useState(null);

  const navigate = useNavigate();
  useEffect(() => {
    fetchFullCandidateReport(candidateId)
      .then((res) => {
        if (res.data.status) {
          setReportData(res.data.data);

        }
      })
      .catch((err) => {

      });
  }, [candidateId]);

  useEffect(() => {
    if (!reportData?.employeeVerificationInfo) return;

    const { addressCheck, educationCheck, employmentCheck, attachmentResponses } = reportData.employeeVerificationInfo;

    // Helper function to load a single proof from S3
    const loadProof = async (proofPath) => {
      if (!proofPath) return null;

      try {
        const fileName = proofPath.split("/").pop();
        const response = await getS3key(proofPath);
        const blobUrl = URL.createObjectURL(response.data);
        return { fileName, url: blobUrl };
      } catch (error) {
        console.error("Error loading proof:", error);
        return null;
      }
    };

    // Load Address Proofs
    if (addressCheck) {
      loadProof(addressCheck.currentAddressVerificationUrl).then((file) =>
        file ? setCurrentAddressProofs([file]) : null
      );
      loadProof(addressCheck.permanentAddressVerificationUrl).then((file) =>
        file ? setPermanentAddressProofs([file]) : null
      );
    }

    // Load Education Proofs grouped by education level
    if (educationCheck && Array.isArray(educationCheck)) {
      const loadAllEducationProofs = async () => {
        const groupedProofs = {}; // { "High School": [{fileName, url, instituteName}], ... }

        for (const edu of educationCheck) {
          if (!edu.educationFileUrl) continue;

          const proof = await loadProof(edu.educationFileUrl);
          if (!proof) continue;

          const level = edu.educationLevel || "Unknown Level";
          const instituteName = edu.instituteName || "";

          if (!groupedProofs[level]) groupedProofs[level] = [];
          groupedProofs[level].push({ ...proof, instituteName });
        }

        setEducationProofs(groupedProofs);
      };

      loadAllEducationProofs();
    }

    if (employmentCheck && Array.isArray(employmentCheck)) {
      const loadAllPhysicalEmploymentCheckProofs = async () => {

        const groupedProofs = {};

        for (const emp of employmentCheck) {

          if (!emp.employmentFileUrl) continue;

          const proof = await loadProof(emp.employmentFileUrl);
          if (!proof) continue;

          const companyName = emp.companyName || "Unknown Company";

          if (!groupedProofs[companyName]) {
            groupedProofs[companyName] = [];
          }

          groupedProofs[companyName].push({
            ...proof,
            companyName
          });
        }

        setEmploymentPhysicalProofs(groupedProofs);
      };

      loadAllPhysicalEmploymentCheckProofs();
    }

    // Load Virtual Employment Proofs (attachmentResponses)
    if (attachmentResponses && Array.isArray(attachmentResponses)) {

      const loadAllVirtualEmploymentProofs = async () => {

        const groupedProofs = {};

        for (const att of attachmentResponses) {

          if (!att.fileUrl) continue;

          const proof = await loadProof(att.fileUrl);
          if (!proof) continue;

          const companyName = att.companyName || "Unknown Company";
          const employeeCode = att.employeeCode || "";

          if (!groupedProofs[companyName]) {
            groupedProofs[companyName] = [];
          }

          groupedProofs[companyName].push({
            ...proof,
            employeeCode
          });

        }

        setEmploymentVirtualProofs(groupedProofs);
      };

      loadAllVirtualEmploymentProofs();
    }
  }, [reportData]);

  const handleBack = () => {
    // navigate("/client/case/total");
    navigate(-1)
  };

  if (!reportData)
    return (
      <div className="centered-message">
        <div className="spinner"></div>
        <div>Loading report...</div>
      </div>
    );

  const { employeeVerificationInfo, adminVerificationResult } = reportData;
  const {
    personalCheck,
    addressCheck,
    educationCheck,
    employmentCheck,
    employmentUANCheckRequests,
    criminalCheckRequests,
    attachmentResponses,
  } = employeeVerificationInfo;



  // const handleDownload = () => {
  //   const element = document.getElementById('final-report-container');
  //   const opt = {
  //     margin:       0.2,
  //     filename:     `Candidate_Report_${candidateId}.pdf`,
  //     image:        { type: 'jpeg', quality: 1 },
  //     html2canvas:  { scale: 2, useCORS: true },
  //     jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
  //   };

  //   html2pdf().set(opt).from(element).save();
  // };

  // Add this line below your other imports (or convert logo to base64)
  // const logoBase64 = 'YOUR_BASE64_STRING_HERE'; // Replace with the actual Base64 string for your logo

  const handleDownload = async () => {
    const reportElement = document.getElementById("final-report-container");
    reportElement.classList.add("pdf-export-mode");

    //  Utility: Convert image URL to Base64 safely
    const loadImageAsBase64 = async (url) => {
      try {
        if (!url) return null;

        // Fetch the image as a blob
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          return null;
        }

        const blob = await response.blob();

        // Convert blob to Base64
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {

        return null;
      }
    };

    //  Convert logo to base64
    const base64Logo = await loadImageAsBase64(logo);

    // Convert all images inside report to base64
    const images = Array.from(reportElement.querySelectorAll("img"));
    await Promise.all(
      images.map((img) =>
        loadImageAsBase64(img.src).then((base64) => {
          if (base64) img.src = base64;
        }),
      ),
    );

    // Wait for DOM update
    await new Promise((r) => setTimeout(r, 300));

    //  Create html2pdf instance
    const opt = {
      margin: [10, 10, 25, 10], // extra bottom margin for footer
      filename: `Candidate_Report_${candidateId}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2, // good quality
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: document.documentElement.offsetWidth,
        letterRendering: true,
        useCORS: true,
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    //  Generate pdf using html2pdf, but before saving — add footer
    const worker = html2pdf().set(opt).from(reportElement);

    // Generate the pdf blob
    const pdf = await worker.toPdf().get("pdf");

    const pageCount = pdf.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Footer line (optional)
      // pdf.setDrawColor(200);
      // pdf.setLineWidth(0.3);
      // pdf.line(10, pageHeight - 15, pageWidth - 10, pageHeight - 15);

      // Page number (left)
      // pdf.setFontSize(10);
      // pdf.setTextColor(80);
      // pdf.text(`Page ${i} of ${pageCount}`, 10, pageHeight - 8);

      // Logo on the right
      if (base64Logo) {
        const logoWidth = 20;
        const logoHeight = 10;
        pdf.addImage(
          base64Logo,
          "PNG",
          pageWidth - logoWidth - 10,
          pageHeight - logoHeight - 12,
          logoWidth,
          logoHeight,
        );
      }
    }

    pdf.save(`Candidate_Report_${candidateId}.pdf`);

    reportElement.classList.remove("pdf-export-mode");
  };

  // helper to style verified text
  // const renderStatusCell = (verificationStatus) => {
  //   if (verificationStatus !== true) return null;

  //   return (
  //     <span className="report-status-verified">
  //       verified
  //     </span>
  //   );
  // };

  const renderStatusCell = (status) => {
    const isVerified =
      status === true ||
      status === "VERIFIED" ||
      status === "Verified" ||
      status === "verified" ||
      status === "COMPLETED";

    if (!isVerified) return null;

    return <span className="report-status-verified">Verified</span>;
  };

  const severityLegend = [
    {
      type: "Clear",
      color: "#388E3C",
      description:
        "No significant disparity between stated and verified antecedents.",
    },
    {
      type: "Minor Discrepant",
      color: "#FBC02D",
      description:
        "Verification reports a mismatch of a lesser degree or lower impact.",
    },
    {
      type: "Insufficient",
      color: "#7B1FA2",
      description:
        "Verification could not be completed due to missing mandatory data or documents.",
    },
    {
      type: "Attention Required",
      color: "#1976D2",
      description:
        "Source is unable or refuses to share a complete response due to lack of data.",
    },
    {
      type: "Discrepant",
      color: "#D32F2F",
      description:
        "Verification reports a major mismatch with stated antecedents or the source is fake.",
    },
    {
      type: "No Response",
      color: "#616161",
      description:
        "No response received from the verification source by the report generation date.",
    },
  ];

  const getStatusTextColor = (status) => {
    const normalized = normalizeStatus(status);

    const match = severityLegend.find((item) => item.type === normalized);

    return match ? match.color : "#000";
  };

  const normalizeStatus = (status) => {
    if (!status) return "";

    return status
      .toLowerCase()
      .replace(/_received$/, "") // removes "_RECEIVED"
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getStarsForStatus = (status) => {
    switch (normalizeStatus(status)) {
      case "Clear":
        return 5;
      case "Minor Discrepant":
        return 4;
      case "Insufficient":
        return 3;
      case "Attention Required":
        return 2;
      case "Discrepant":
        return 1;
      case "No Response":
        return 0;
      default:
        return 0;
    }
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const yOffset = -80;
      const y =
        section.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const isSameAddress =
    (addressCheck.currentAddressLine1 || "") === (addressCheck.permanentAddressLine1 || "") &&
    (addressCheck.currentAddressLine2 || "") === (addressCheck.permanentAddressLine2 || "") &&
    (addressCheck.currentCity || "") === (addressCheck.permanentCity || "") &&
    (addressCheck.currentState || "") === (addressCheck.permanentState || "") &&
    (addressCheck.currentPinCode || "") === (addressCheck.permanentPinCode || "");

  return (
    <div className="final-container">
      <div className="final-report-back">
        <button className="report-back-button" onClick={handleBack}>
          ← Back
        </button>
      </div>

      <div className="final-report-container" id="final-report-container">
        <div className="report-header-section">
          <div className="report-header-left">
            <img src={logo} alt="Company Logo" className="report-logo" />
          </div>
          {/* <h1 className="report-title">Candidate Verification Report</h1> */}

          <div className="report-actions">
            <button onClick={handleDownload} className="report-action-button">
              <Download size={18} /> Download
            </button>
          </div>
        </div>
        <div className="name-header">
          <h3>
            BGC Report for {personalCheck?.firstName} {personalCheck?.lastName}
          </h3>
        </div>

        <div className="report-table-container first-table">
          <table className="report-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Report Date</th>
                <th>Client</th>
                <th>Report Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{personalCheck?.candidateId}</td>
                <td>{personalCheck?.firstName}</td>
                <td>{personalCheck?.lastName}</td>
                <td>{adminVerificationResult?.reportDate}</td>
                <td>Client Server Technology Solutions pvt ltd</td>
                <td
                  style={{
                    color: getStatusTextColor(
                      adminVerificationResult?.verificationStatus,
                    ),
                    fontWeight: "bold",
                  }}
                >
                  {normalizeStatus(
                    adminVerificationResult?.verificationStatus,
                  ) || "N/A"}

                  <br></br>
                  <span className="status-stars" style={{ marginLeft: "5px" }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <span
                        key={index}
                        className={`star ${index <
                          getStarsForStatus(
                            adminVerificationResult?.verificationStatus,
                          )
                          ? "filled"
                          : ""
                          }`}
                      >
                        ★
                      </span>
                    ))}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="report-details-section">
          <div className="details-card">
            <h3 className="details-card-title">Candidate Details:</h3>
            <div className="details-row">
              <span className="details-label">Name : </span>
              <span className="details-value">
                {personalCheck?.firstName} {personalCheck?.lastName}
              </span>
            </div>
            <div className="details-row">
              <span className="details-label">Email : </span>
              <span className="details-value">{personalCheck?.email}</span>
            </div>
          </div>
          <div className="details-card">
            <h3 className="details-card-title">Verifier Details : </h3>
            <div className="details-row">
              <span className="details-label">Name : </span>
              <span className="details-value">
                {adminVerificationResult?.verifierName}
              </span>
            </div>
            <div className="details-row">
              <span className="details-label">Email : </span>
              <span className="details-value">
                {adminVerificationResult?.verifierEmail}
              </span>
            </div>
          </div>
        </div>

        {/* <hr className="hr-line" /> */}
        <div className="severity-legend-section">
          <h2 className="legend-title">Severity Legend</h2>
          <div className="legend-grid">
            {severityLegend.map((item, idx) => (
              <div
                key={idx}
                className="client-legend-item-report"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  marginBottom: "12px",
                }}
              >
                {/* Color Box */}
                <div
                  className="legend-color-box-report"
                  style={{
                    backgroundColor: item.color,
                    width: "20px",
                    height: "20px",
                    borderRadius: "3px",
                    flexShrink: 0,
                  }}
                ></div>

                {/* Content */}
                <div className="legend-content-report">
                  <p
                    className="legend-type-report"
                    style={{ margin: 0, fontWeight: "bold" }}
                  >
                    {item.type}
                  </p>
                  <p
                    className="legend-description-report"
                    style={{
                      margin: "2px 0 4px 0",
                      fontSize: "13px",
                      color: "#555",
                    }}
                  >
                    {item.description}
                  </p>

                  {/* Stars */}
                  <div className="legend-stars-report">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span
                        key={i}
                        className={`star ${i < getStarsForStatus(item.type) ? "filled" : ""
                          }`}
                        style={{
                          color:
                            i < getStarsForStatus(item.type)
                              ? "#FFD700"
                              : "#CCC",
                          fontSize: "14px",
                          marginRight: "2px",
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="report-header">
          <h1 className="final-report-title">FINAL BACKGROUND REPORT</h1>
        </div>

        {/* TABLE OF CONTENTS */}
        <div className="report-section table-of-contents">
          <h2 className="report-main-title">Executive Synopsis</h2>
          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Reference Link</th>
                </tr>
              </thead>
              <tbody>
                {personalCheck && (
                  <tr>
                    <td>Personal Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("personal-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}
                {addressCheck && (
                  <tr>
                    <td>Address Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("address-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}
                {educationCheck && (
                  <tr>
                    <td>Education Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("education-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}

                {employmentCheck && (
                  <tr>
                    <td>Employment Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("employment-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}

                {criminalCheckRequests?.length > 0 && (
                  <tr>
                    <td>Criminal Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("criminal-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}
                {employmentUANCheckRequests?.length > 0 && (
                  <tr>
                    <td>UAN / EPF Report</td>
                    <td>
                      <button
                        className="toc-link"
                        onClick={() => scrollToSection("uan-report")}
                      >
                        Status - (REFER ANNEXURE)
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PERSONAL REPORT */}
        <div className="report-section" id="personal-report">
          <h2 className="report-main-title">PERSONAL REPORT</h2>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Stated / Verified</th>
                </tr>
              </thead>
              <tbody>
                {personalCheck ? (
                  <>
                    <tr>
                      <td>First Name</td>
                      <td>{personalCheck.firstName || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Last Name</td>
                      <td>{personalCheck.lastName || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Date of Birth</td>
                      <td>{personalCheck.dob || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Father Name</td>
                      <td>{personalCheck.fatherName || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Email</td>
                      <td>{personalCheck.email || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Mobile</td>
                      <td>{personalCheck.mobileNumber || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Remarks</td>
                      <td>{personalCheck.remarks || "N/A"}</td>
                    </tr>
                    <tr>
                      <td>Verification Status</td>
                      <td>
                        {renderStatusCell(personalCheck.verificationStatus)}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan={2}
                      style={{
                        textAlign: "center",
                        fontStyle: "italic",
                        color: "#777",
                      }}
                    >
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ADDRESS REPORT */}
        {addressCheck && (
          <>
            <div className="report-section" id="address-report">
              <h2 className="report-main-title">ADDRESS REPORT</h2>
              <div className="report-table-container">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isSameAddress ? (
                      <tr>
                        <td>Address</td>
                        <td>{`${addressCheck.currentAddressLine1 || ""}, ${addressCheck.currentAddressLine2 || ""
                          }, ${addressCheck.currentCity || ""}, ${addressCheck.currentState || ""
                          }, ${addressCheck.currentPinCode || ""}`}</td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td>Current Address</td>
                          <td>{`${addressCheck.currentAddressLine1 || ""}, ${addressCheck.currentAddressLine2 || ""
                            }, ${addressCheck.currentCity || ""}, ${addressCheck.currentState || ""
                            }, ${addressCheck.currentPinCode || ""}`}</td>
                        </tr>
                        <tr>
                          <td>Permanent Address</td>
                          <td>{`${addressCheck.permanentAddressLine1 || ""}, ${addressCheck.permanentAddressLine2 || ""
                            }, ${addressCheck.permanentCity || ""}, ${addressCheck.permanentState || ""
                            }, ${addressCheck.permanentPinCode || ""}`}</td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td>Remarks</td>
                      <td>{addressCheck.remarks}</td>
                    </tr>
                    <tr>
                      <td>Verification Status</td>
                      <td>
                        {renderStatusCell(addressCheck.verificationStatus)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {(addressCheck?.currentAddressVerificationUrl ||
                addressCheck?.permanentAddressVerificationUrl) && (
                  <div className="attachments-preview-container">


                    {isSameAddress ? (
                      currentAddressProofs.length > 0 && (
                        <div className="attachment-preview-container">
                          <p className="attachment-label">
                            <strong>Address Proof</strong>
                          </p>

                          {currentAddressProofs.map((fileObj, idx) => {
                            const isPdf = fileObj.fileName.toLowerCase().endsWith(".pdf");

                            return (
                              <div key={idx} className="attachment-box">
                                {isPdf ? (
                                  <PDFView pdfUrl={fileObj.url} />
                                ) : (
                                  <img
                                    src={fileObj.url}
                                    alt={`Address Proof ${idx + 1}`}
                                    className="attachment-preview"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )
                    ) : (
                      <>

                        {/* CURRENT ADDRESS PROOF */}
                        {currentAddressProofs.length > 0 && (
                          <div className="attachment-preview-container">
                            <p className="attachment-label">
                              <strong>Current Address Proof</strong>
                            </p>

                            {currentAddressProofs.map((fileObj, idx) => {
                              const isPdf = fileObj.fileName.toLowerCase().endsWith(".pdf");

                              return (
                                <div key={idx} className="attachment-box">
                                  {isPdf ? (
                                    <PDFView pdfUrl={fileObj.url} />
                                  ) : (
                                    <img
                                      src={fileObj.url}
                                      alt={`Current Address Proof ${idx + 1}`}
                                      className="attachment-preview"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* PERMANENT ADDRESS PROOF */}
                        {permanentAddressProofs.length > 0 && (
                          <div className="attachment-preview-container">
                            <p className="attachment-label">
                              <strong>Permanent Address Proof</strong>
                            </p>

                            {permanentAddressProofs.map((fileObj, idx) => {
                              const isPdf = fileObj.fileName.toLowerCase().endsWith(".pdf");

                              return (
                                <div key={idx} className="attachment-box">
                                  {isPdf ? (
                                    <PDFView pdfUrl={fileObj.url} />
                                  ) : (
                                    <img
                                      src={fileObj.url}
                                      alt={`Permanent Address Proof ${idx + 1}`}
                                      className="attachment-preview"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

            </div>
          </>
        )}



        {educationCheck && (
          <>
            <div className="report-section" id="education-report">
              <h2 className="report-main-title">Education REPORT</h2>
              <div className="report-table-container">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Qualification</th>
                      <th>Institute</th>
                      <th>Enrolled Year</th>
                      <th>Passing Year</th>
                      <th>Verification Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {educationCheck.map((edu, index) => (
                      <tr key={index} className="education-main-row">
                        <td>{edu.educationLevel || "-"}</td>
                        <td>{edu.instituteName || "-"}</td>
                        <td>{edu.enrolledYear || "-"}</td>
                        <td>{edu.passingYear || "-"}</td>
                        <td>{renderStatusCell(edu.verificationStatus)}</td>
                      </tr>
                    ))}


                    {educationCheck?.length > 0 &&
                      educationCheck[0]?.remarks &&
                      educationCheck[0].remarks.trim() !== "" && (
                        <tr className="education-remarks-row">
                          <td colSpan={2} className="remarks-label">
                            Remarks
                          </td>

                          <td colSpan={3} className="remarks-value">
                            {educationCheck[0].remarks &&
                              educationCheck[0].remarks.trim() !== ""
                              ? educationCheck[0].remarks
                              : "—"}
                          </td>
                        </tr>
                      )}
                  </tbody>
                </table>
              </div>

              <div className="attachments-preview-container">
                <div className="attachments-section">

                  {educationProofs && Object.keys(educationProofs).length > 0 && (
                    <div className="attachments-preview-container">
                      {Object.keys(educationProofs).length > 0 && (
                        <div className="attachments-preview-container">

                          {Object.entries(educationProofs).map(([level, files]) => (
                            <div key={level} className="attachment-preview-container">

                              <p className="attachment-label">
                                <strong>{level.replaceAll("_", " ")}</strong>
                              </p>

                              {files.map((fileObj, idx) => {
                                const isPdf = fileObj.fileName?.toLowerCase().endsWith(".pdf");

                                return (
                                  <div key={idx} className="attachment-box">

                                    <p className="attachment-institute">
                                      {fileObj.instituteName}
                                    </p>

                                    {isPdf ? (
                                      <PDFView pdfUrl={fileObj.url} />
                                    ) : (
                                      <img
                                        src={fileObj.url}
                                        alt={`Education Proof ${idx + 1}`}
                                        className="attachment-preview"
                                      />
                                    )}

                                  </div>
                                );
                              })}

                            </div>
                          ))}

                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>

            </div>
          </>
        )}


        {employmentCheck?.length > 0 && (
          <div className="report-section" id="employment-report">
            <h2 className="report-main-title">EMPLOYMENT REPORT</h2>

            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Employee Code</th>
                    <th>Company Name</th>
                    <th>Job Title</th>
                    <th>Location</th>
                    <th>HR Contact</th>
                    <th>Working From</th>
                    <th>Working To</th>
                    <th>Remarks</th>
                    <th>Verification Status</th>
                  </tr>
                </thead>

                <tbody>
                  {employmentCheck.map((emp, index) => (
                    <tr key={index}>
                      <td>{emp.employeeCode || "N/A"}</td>
                      <td>{emp.companyName || "N/A"}</td>
                      <td>{emp.jobTitle || "N/A"}</td>
                      <td>{emp.location || "N/A"}</td>
                      <td>
                        {emp.hrContactEmail || "N/A"} <br />
                        {emp.hrContactNumber || ""}
                      </td>
                      <td>{emp.workingFrom || "N/A"}</td>
                      <td>{emp.workingTo || "N/A"}</td>
                      <td>{emp.remarks || "N/A"}</td>
                      <td>{renderStatusCell(emp.individualStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Attachments OUTSIDE table */}
            {employmentCheck.map((emp, index) => (
              <div key={index} className="attachments-preview-container">

                {employmentPhysicalProofs &&
                  employmentPhysicalProofs[emp.companyName] && (
                    <>
                      <p className="attachment-label">
                        <strong>{emp.companyName}</strong>
                      </p>

                      {employmentPhysicalProofs[emp.companyName].map((fileObj, idx) => {

                        const isPdf = fileObj.fileName
                          ?.toLowerCase()
                          .endsWith(".pdf");

                        return (
                          <div key={idx} className="attachment-box">

                            {isPdf ? (
                              <PDFView pdfUrl={fileObj.url} />
                            ) : (
                              <img
                                src={fileObj.url}
                                alt={`Employment Proof ${idx + 1}`}
                                className="attachment-preview"
                              />
                            )}

                          </div>
                        );
                      })}
                    </>
                  )}

              </div>
            ))}

          </div>
        )}


        {attachmentResponses?.length > 0 && (
          <div className="report-section" id="attachments-report">

            <h2 className="report-main-title">VIRTUAL EMPLOYMENT REPORT</h2>

            <div className="report-table-container">
              <table className="report-table">

                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Employee Code</th>
                    <th>Remarks</th>
                    <th>Verification Status</th>
                  </tr>
                </thead>

                <tbody>
                  {attachmentResponses.map((att, idx) => (
                    <tr key={idx}>
                      <td>{att.companyName || "N/A"}</td>
                      <td>{att.employeeCode || "N/A"}</td>
                      <td>{att.remarks || "N/A"}</td>
                      <td>{renderStatusCell(att.isVerified)}</td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>

            {/* Attachment preview */}
            <div className="attachments-preview-container">

              {Object.entries(employmentVirtualProofs).map(([company, files]) => (

                <div key={company} className="attachment-preview-item">

                  <p className="attachment-label">
                    <strong>{company}</strong>
                  </p>

                  <div className="attachment-preview-container">

                    {files.map((fileObj, idx) => {

                      const isPdf = fileObj.fileName
                        ?.toLowerCase()
                        .endsWith(".pdf");

                      return (
                        <div key={idx} className="attachment-box">

                          {isPdf ? (
                            <PDFView pdfUrl={fileObj.url} />
                          ) : (
                            <img
                              src={fileObj.url}
                              alt={`Virtual Employment Proof`}
                              className="attachment-preview"
                            />
                          )}

                        </div>
                      );
                    })}

                  </div>
                </div>

              ))}

            </div>

          </div>
        )}

        {/* CRIMINAL REPORT */}
        <div className="report-section" id="criminal-report">
          <h2 className="report-main-title">CRIMINAL REPORT</h2>
          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Petitioner</th>
                  <th>Respondent</th>
                  <th>State</th>
                  <th>Case Type</th>
                  <th>Case Name</th>
                  <th>Case Number</th>
                  <th>Filing Number</th>
                  <th>Case Status</th>
                  <th>FIR Link</th>
                </tr>
              </thead>
              <tbody>
                {criminalCheckRequests && criminalCheckRequests.length > 0 ? (
                  criminalCheckRequests.map((criminal, index) => (
                    <tr key={index}>
                      <td>{criminal.year || "N/A"}</td>
                      <td>{criminal.petitioner || "N/A"}</td>
                      <td>{criminal.respondent || "N/A"}</td>
                      <td>{criminal.state || "N/A"}</td>
                      <td>{criminal.caseType || "N/A"}</td>
                      <td>{criminal.caseName || "N/A"}</td>
                      <td>{criminal.caseNumber || "N/A"}</td>
                      <td>{criminal.filingNumber || "N/A"}</td>
                      <td>{criminal.caseStatus || "N/A"}</td>
                      <td>
                        {criminal.firLink ? (
                          <a
                            href={criminal.firLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View FIR
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      style={{
                        textAlign: "center",
                        fontStyle: "italic",
                        color: "#777",
                      }}
                    >
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* UAN / EPF REPORT */}
        {employmentUANCheckRequests?.length > 0 && (
          <>
            <div className="report-section" id="uan-report">
              <h2 className="report-main-title">UAN / EPF REPORT</h2>
              <div className="report-table-container">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>UAN</th>
                      <th>Name</th>
                      <th>Member ID</th>
                      <th>DOJ EPF</th>
                      <th>DOE EPF</th>
                      <th>Company Name</th>
                      <th>Month History</th>
                      <th>Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employmentUANCheckRequests.map((uan, index) => (
                      <tr key={index}>
                        <td>{uan.uan}</td>
                        <td>{uan.name}</td>
                        <td>{uan.memberId}</td>
                        <td>{uan.dojEpf}</td>
                        <td>{uan.doeEpf}</td>
                        <td>{uan.companyName}</td>
                        <td>{uan.monthHistory}</td>
                        <td>{renderStatusCell(uan.verified)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FinalReportPage;
